import React from 'react';
import { useApp } from '../context/AppContext';
import { getColors, fonts } from '../theme';
import { ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import Footer from '../components/Footer';

const PrivacyPage = () => {
  const { isDark } = useApp();
  const colors = getColors(isDark);
  const navigate = useNavigate();

  const sections = [
    {
      title: '1. Information We Collect',
      content: `We collect information you provide directly to us when you create an account, including your email address and authentication credentials. We also collect data you input into the application, such as habits, tasks, goals, and related notes. Additionally, we automatically collect certain technical information when you use the Service, including device type, browser type, and usage patterns.`
    },
    {
      title: '2. How We Use Your Information',
      content: `We use the information we collect to: (a) provide, maintain, and improve the Service; (b) authenticate your identity and secure your account; (c) send you technical notices and support messages; (d) respond to your comments, questions, and requests; (e) analyze usage patterns to improve user experience.`
    },
    {
      title: '3. Data Storage and Security',
      content: `Your data is stored securely using industry-standard encryption methods. We use Microsoft Azure cloud services to host and protect your data. We implement appropriate technical and organizational measures to protect your personal information against unauthorized access, alteration, disclosure, or destruction.`
    },
    {
      title: '4. Data Sharing',
      content: `We do not sell, trade, or rent your personal information to third parties. We may share your information only in the following circumstances: (a) with your consent; (b) to comply with legal obligations; (c) to protect our rights and safety; (d) with service providers who assist in operating our Service (subject to confidentiality agreements).`
    },
    {
      title: '5. Your Rights',
      content: `You have the right to: (a) access the personal data we hold about you; (b) request correction of inaccurate data; (c) request deletion of your data; (d) export your data in a portable format; (e) withdraw consent for data processing. To exercise these rights, please contact us through the application.`
    },
    {
      title: '6. Data Retention',
      content: `We retain your personal data for as long as your account is active or as needed to provide you with the Service. If you delete your account, we will delete your personal data within 30 days, except where we are required to retain it for legal or legitimate business purposes.`
    },
    {
      title: '7. Cookies and Tracking',
      content: `We use essential cookies to maintain your session and authentication state. We do not use tracking cookies or third-party analytics that track you across other websites. You can configure your browser to refuse cookies, but this may limit your ability to use certain features of the Service.`
    },
    {
      title: '8. Children\'s Privacy',
      content: `The Service is not intended for children under the age of 13. We do not knowingly collect personal information from children under 13. If we become aware that we have collected personal information from a child under 13, we will take steps to delete such information.`
    },
    {
      title: '9. International Data Transfers',
      content: `Your information may be transferred to and processed in countries other than your own. We ensure that appropriate safeguards are in place to protect your data in accordance with applicable data protection laws.`
    },
    {
      title: '10. Changes to This Policy',
      content: `We may update this Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy on this page and updating the "Last updated" date. Your continued use of the Service after changes constitutes acceptance of the updated policy.`
    },
    {
      title: '11. Contact Us',
      content: `If you have any questions about this Privacy Policy or our data practices, please contact us through the application or at our official support channels.`
    }
  ];

  return (
    <div 
      className="min-h-screen py-12 px-6"
      style={{ backgroundColor: colors.bg }}
    >
      <div className="max-w-3xl mx-auto">
        {/* Back Button */}
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 mb-8 text-sm transition-all duration-200 hover:opacity-70"
          style={{ color: colors.accent }}
        >
          <ArrowLeft size={16} />
          Back
        </button>

        {/* Header */}
        <div className="mb-12">
          <h1 
            className="text-4xl mb-4 font-semibold tracking-[-0.035em]"
            style={{ color: colors.textPrimary, }}
          >
            Privacy Policy
          </h1>
          <p className="text-sm" style={{ color: colors.textMuted }}>
            Last updated: December 24, 2024
          </p>
        </div>

        {/* Introduction */}
        <div 
          className="pt-5 pb-2 mb-8"
          style={{ borderTop: `1px solid ${colors.textPrimary}` }}
        >
          <p className="text-sm leading-relaxed" style={{ color: colors.textSecondary }}>
            At mind., operated by Mill Creek Media LLC (Fort Collins, CO), we take your privacy seriously. This Privacy Policy explains how we collect, use, 
            disclose, and safeguard your information when you use our service. Please read this policy 
            carefully to understand our practices regarding your personal data.
          </p>
        </div>

        {/* Sections */}
        <div className="space-y-8">
          {sections.map((section, index) => (
            <div key={index}>
              <h2 
                className="text-lg mb-3 font-semibold tracking-tight"
                style={{ color: colors.textPrimary }}
              >
                {section.title}
              </h2>
              <p 
                className="text-sm leading-relaxed"
                style={{ color: colors.textSecondary }}
              >
                {section.content}
              </p>
            </div>
          ))}
        </div>

        <Footer />
      </div>
    </div>
  );
};

export default PrivacyPage;
